import { useState } from "react";
import { Mail, Plus, X, Save, Loader2 } from "lucide-react";
import { V } from "@/lib/vocab";

/* A list of email addresses: chips + one input. Enter or comma adds. */
export function EmailChips({ value = [], onChange, disabled, placeholder = "accounting@yourcompany.com", testid = "cc-emails" }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const e = draft.trim().toLowerCase().replace(/,$/, "");
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return;
    if (!value.includes(e)) onChange([...value, e]);
    setDraft("");
  };
  return (
    <div data-testid={testid} className="flex flex-wrap items-center gap-1.5 rounded-xl border border-sand-300 bg-white px-2 py-1.5">
      {value.map((e) => (
        <span key={e} data-testid={`${testid}-chip`} className="inline-flex items-center gap-1 rounded-full bg-sand-100 px-2.5 py-1 text-xs font-semibold text-ink/75">
          {e}
          {!disabled && (
            <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange(value.filter((x) => x !== e))} className="text-ink/40 hover:text-redline-600" aria-label={`Remove ${e}`}>
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input data-testid={`${testid}-input`} value={draft} placeholder={value.length ? "Add another…" : placeholder} inputMode="email"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          onBlur={add}
          className="min-w-[12rem] flex-1 bg-transparent px-1.5 py-1 text-sm outline-none" />
      )}
      {!disabled && draft.trim() && (
        <button type="button" data-testid={`${testid}-add`} onClick={add} className="rounded-lg bg-ink p-1 text-white"><Plus className="h-3.5 w-3.5" /></button>
      )}
    </div>
  );
}

/* Field Services → Settings: "Who's copied on job emails" (iter371). */
export function JobEmailCcCard({ st, setSt, staff, readOnly, busy, onSave, companyName }) {
  const on = st.schedule_email_enabled !== false;
  const picks = st.schedule_cc_users || [];
  const team = (staff || []).filter((s) => s.email && ["hoa_manager", "regional_manager", "company_admin", "board_member"].includes(s.role));
  return (
    <div data-testid="job-email-cc-card" className="rounded-2xl border border-sand-200 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-bold text-ink"><Mail className="h-4 w-4 text-ink/45" /> Who's copied on job emails</div>
          <p className="mt-1 text-xs leading-relaxed text-ink/55">
            When a job is confirmed, one email with every detail (scope, price, start, contract, who to call) goes to you and the company's
            office. The people below are copied on that email — and on the award, the signed contract and each approved invoice.
          </p>
        </div>
        <label className="mt-1 inline-flex shrink-0 cursor-pointer items-center gap-2 text-xs font-semibold text-ink">
          <input type="checkbox" data-testid="cc-enabled" checked={on} disabled={readOnly || busy}
            onChange={(e) => setSt({ ...st, schedule_email_enabled: e.target.checked })} />
          {on ? "On" : "Off"}
        </label>
      </div>

      {on && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-ink/45">From the team</div>
            <label className="flex items-center gap-2.5 rounded-xl border border-sand-200 px-3 py-2 text-sm text-ink/75">
              <input type="checkbox" data-testid="cc-board" checked={st.schedule_email_board !== false} disabled={readOnly}
                onChange={(e) => setSt({ ...st, schedule_email_board: e.target.checked })} />
              Every {V.lc("board")}
            </label>
            <label className="flex items-center gap-2.5 rounded-xl border border-sand-200 px-3 py-2 text-sm text-ink/75">
              <input type="checkbox" data-testid="cc-managers" checked={!!st.schedule_email_managers} disabled={readOnly}
                onChange={(e) => setSt({ ...st, schedule_email_managers: e.target.checked })} />
              Every manager on this {V.lc("org")}
            </label>
            {team.map((s) => (
              <label key={s.user_id} data-testid={`cc-user-${s.user_id}`} className="flex items-center gap-2.5 rounded-xl border border-sand-200 px-3 py-2">
                <input type="checkbox" checked={picks.includes(s.user_id)} disabled={readOnly}
                  onChange={(e) => setSt({ ...st, schedule_cc_users: e.target.checked ? [...picks, s.user_id] : picks.filter((x) => x !== s.user_id) })} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">{s.name}</span>
                  <span className="block truncate text-xs text-ink/45">{s.email} · {String(s.role).replace(/_/g, " ")}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Anyone else — accounting, a bookkeeper, a partner</div>
            <EmailChips value={st.schedule_cc || []} disabled={readOnly} onChange={(v) => setSt({ ...st, schedule_cc: v })} />
            <p className="text-xs leading-relaxed text-ink/45">
              Up to 10 addresses. {companyName ? <>{companyName} can also set a standing copy for all its {V.lcs("org")} under Portfolio → Field Services.</> : ""}
            </p>
          </div>
        </div>
      )}

      {!readOnly && (
        <button data-testid="cc-save" disabled={busy} onClick={onSave}
          className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      )}
    </div>
  );
}
